import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useListMeetingEmployeesQuery,
  useImportMeetingEmployeesMutation,
  useSyncMeetingEmployeesMutation,
  useDeleteMeetingEmployeeMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';

/* Справочник сотрудников ОСНОВА.Совещания (0157). DeepSeek определяет участников
   совещания из расшифровки, код сопоставляет их с этим справочником и подтягивает
   vk_chat_id → авто-добавление в участники (адресная рассылка) и поручения. */

const Card = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(5)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const SectionLabel = styled.h2`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 0.6fr auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 14px;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13.5px;
  font-family: ui-monospace, monospace;
  resize: vertical;
`;

const Btn = styled.button`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const SyncBtn = styled.button`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const DeleteBtn = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: 13px;

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Ok = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Hint = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13.5px;
  line-height: 1.55;
`;

interface ParsedRow {
  full_name: string;
  vk_chat_id?: string | null;
  department?: string | null;
  position?: string | null;
}

/** Разбор строк «ФИО; chat_id; отдел; должность» (разделитель ; или таб). Пустые
 *  поля/строки пропускаются; строки без ФИО игнорируются. */
function parseRows(text: string): ParsedRow[] {
  const out: ParsedRow[] = [];
  for (const line of text.split('\n')) {
    const raw = line.trim();
    if (!raw) continue;
    const cells = raw.split(/[;\t]/).map((c) => c.trim());
    const full_name = cells[0] ?? '';
    if (!full_name) continue;
    out.push({
      full_name,
      vk_chat_id: cells[1] || null,
      department: cells[2] || null,
      position: cells[3] || null,
    });
  }
  return out;
}

export function MeetingsEmployees() {
  const { can_write } = useAgentPermission('meetings');
  const { data, isLoading } = useListMeetingEmployeesQuery();
  const [importEmployees, { isLoading: importing }] = useImportMeetingEmployeesMutation();
  const [syncEmployees, { isLoading: syncing }] = useSyncMeetingEmployeesMutation();
  const [deleteEmployee] = useDeleteMeetingEmployeeMutation();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const employees = data?.employees ?? [];

  async function onSyncUsers() {
    setError(null);
    setOkMsg(null);
    try {
      const res = await syncEmployees().unwrap();
      setOkMsg(`Синхронизировано с пользователями системы: ${res.synced}`);
    } catch {
      setError('Не удалось синхронизировать со списком пользователей');
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    const rows = parseRows(text);
    if (rows.length === 0) {
      setError('Не распознано ни одной строки. Формат: ФИО; chat_id; отдел; должность');
      return;
    }
    try {
      const res = await importEmployees({ employees: rows }).unwrap();
      setOkMsg(`Загружено записей: ${res.upserted}`);
      setText('');
    } catch (err) {
      setError(
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
          'Не удалось загрузить справочник',
      );
    }
  }

  if (!can_write) {
    return <Hint>Управление справочником сотрудников доступно пользователям с правом записи.</Hint>;
  }

  return (
    <>
      <Card>
        <SectionHead>
          <SectionLabel>Справочник сотрудников ({employees.length})</SectionLabel>
          <SyncBtn type="button" onClick={() => void onSyncUsers()} disabled={syncing}>
            {syncing ? 'Синхронизация…' : 'Подтянуть пользователей системы'}
          </SyncBtn>
        </SectionHead>
        <Hint>
          Участники и исполнители поручений из протокола автоматически сопоставляются с этим
          справочником (включая склонения фамилий и инициалы), подтягиваются их учётки и контакты
          для уведомлений.
        </Hint>
        {isLoading ? (
          <Hint>Загрузка…</Hint>
        ) : employees.length === 0 ? (
          <Hint>Справочник пуст — нажмите «Подтянуть пользователей системы» или загрузите список ниже.</Hint>
        ) : (
          employees.map((emp) => (
            <Row key={emp.id}>
              <span>{emp.full_name}</span>
              <Muted>{emp.vk_chat_id ?? '— нет chat_id —'}</Muted>
              <Muted>{emp.department ?? ''}</Muted>
              <Muted>{emp.position ?? ''}</Muted>
              <DeleteBtn type="button" onClick={() => void deleteEmployee(emp.id)}>
                Удалить
              </DeleteBtn>
            </Row>
          ))
        )}
      </Card>

      <Card>
        <SectionLabel>Массовая загрузка</SectionLabel>
        <Hint>
          По одной записи на строку в формате{' '}
          <strong>ФИО; chat_id; отдел; должность</strong> (разделитель «;» или табуляция; лишние
          поля можно опустить). Повторная загрузка обновляет существующие записи по ФИО.
        </Hint>
        <form onSubmit={onSubmit}>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Иванов Иван Иванович; 12345; Юридический отдел; Юрист\nПетров Пётр; 67890; ПТО; Инженер'}
          />
          <Btn type="submit" disabled={importing || !text.trim()}>
            {importing ? 'Загрузка…' : 'Загрузить'}
          </Btn>
        </form>
        {error && <Err>{error}</Err>}
        {okMsg && <Ok>{okMsg}</Ok>}
      </Card>
    </>
  );
}
